import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  InputLabel,
  Skeleton,
  Typography,
  Tabs,
  Tab,
  Grid,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import useStyles from "./NIISensitivity.styles";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import { useUserContext } from "../../../context/UserContext";
import {
  MEASURE,
  DIMENSION,
  ADVANCED_NII,
  ADVANCED_NII_ACCOUNT_NAMES,
  ADVANCED_NII_PROPERTY_TYPES,
  ADVANCED_NII_TAB_CONFIG,
  TWELVE_MOHTHS_EARNING_AT_RISK,
  LATEST_ANNUAL_NII_FORECAST,
  HUNDRED_BP_PARALLEL_SHIFT,
} from "../../../utils/constants";
import ProgressSteps from "../../common/Steps/ProgressSteps";
import { useApprovalContext } from "../../../context/ApprovalContext";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import AdvancedNIISensitivityExport from "./AdvancedNIISensitivityExport";
import {
  applyFilterForValues,
  getDisplayReportingAccountName,
} from "../../../utils/utils";

const getAccountNodeByPropertyType = (datas, propertyType) => {
  const filteredPropertyType = datas?.find(
    (data) => data.propertyType === propertyType,
  );
  return filteredPropertyType?.accountNode || [];
};

const addTotalIncomeSensitivityRow = (rows, summaryLabel) => {
  const receivablesRow = rows?.find(
    (row) =>
      row.rawReportingAccountName ===
      ADVANCED_NII_ACCOUNT_NAMES.TOTAL_RECEIVABLES,
  );

  const payablesRow = rows?.find(
    (row) =>
      row.rawReportingAccountName === ADVANCED_NII_ACCOUNT_NAMES.TOTAL_PAYABLES,
  );

  if (!receivablesRow || !payablesRow) return rows || [];

  const allKeys = new Set([
    ...Object.keys(receivablesRow?.datas || {}),
    ...Object.keys(payablesRow?.datas || {}),
  ]);

  const totalDatas = {};
  allKeys.forEach((key) => {
    totalDatas[key] =
      Number(receivablesRow?.datas?.[key] || 0) +
      Number(payablesRow?.datas?.[key] || 0);
  });

  const totalRow = {
    id: `total-income-sensitivity-${summaryLabel}`,
    accountNum: null,
    rawReportingAccountName: summaryLabel,
    reportingAccountName: summaryLabel,
    formattedReportingAccountName: summaryLabel,
    ...totalDatas,
    datas: totalDatas,
    cellStyles: {
      fontWeight: "bold",
      backgroundColor: "#fcfcfc",
    },
    children: [],
  };

  const filteredRows = rows.filter(
    (row) =>
      row.rawReportingAccountName !==
        ADVANCED_NII_ACCOUNT_NAMES.TOTAL_RECEIVABLES &&
      row.rawReportingAccountName !== ADVANCED_NII_ACCOUNT_NAMES.TOTAL_PAYABLES,
  );

  return [
    {
      ...receivablesRow,
      reportingAccountName: getDisplayReportingAccountName(
        receivablesRow.reportingAccountName,
      ),
    },
    {
      ...payablesRow,
      reportingAccountName: getDisplayReportingAccountName(
        payablesRow.reportingAccountName,
      ),
    },
    ...filteredRows,
    totalRow,
  ];
};

const getLatestAnnualNIIForecast = (datas) => {
  const balanceSection = datas?.find(
    (data) => data.propertyType === ADVANCED_NII_PROPERTY_TYPES.BALANCE,
  );
  const latestAnnualNode =
    balanceSection?.accountNode?.find(
      (node) =>
        node.reportingAccountName ===
        ADVANCED_NII_ACCOUNT_NAMES.LATEST_ANNUAL_NII_FORECAST,
    ) || null;

  const firstValue = latestAnnualNode
    ? Object.values(latestAnnualNode.datas || {})?.[0]
    : 0;

  return {
    node: latestAnnualNode,
    value: Number(firstValue || 0),
  };
};

const flattenReportData = (
  nestedData,
  selectedFigureDivisor,
  selectedDecimalsIn,
  excludeReportingAccountNames = [],
  summaryLabel = null,
) => {
  if (!Array.isArray(nestedData)) return [];

  const divisor = parseFloat(selectedFigureDivisor) || 1;
  const shouldDivide = divisor !== 1;
  const decimals =
    selectedDecimalsIn !== null && selectedDecimalsIn !== undefined
      ? parseInt(selectedDecimalsIn, 10)
      : null;
  const shouldFormatDecimals = decimals !== null && !isNaN(decimals);

  const processNode = (node) => {
    if (
      excludeReportingAccountNames.includes(node?.reportingAccountName) ||
      excludeReportingAccountNames.includes(node?.formattedReportingAccountName)
    ) {
      return null;
    }

    const formattedDatas = {};
    if (node?.datas) {
      Object.keys(node.datas).forEach((key) => {
        let value = Number(node.datas[key] || 0);
        if (shouldDivide) {
          value = value / divisor;
        }
        formattedDatas[key] = shouldFormatDecimals
          ? Number(value.toFixed(decimals))
          : value;
      });
    }

    const isSummaryRow =
      summaryLabel &&
      node.reportingAccountName === summaryLabel &&
      node.formattedReportingAccountName === summaryLabel;

    const rawReportingAccountName = node.reportingAccountName;
    const displayReportingAccountName = getDisplayReportingAccountName(
      isSummaryRow ? summaryLabel : rawReportingAccountName,
    );

    return {
      ...node,
      id: `${node.accountNum}-${node.reportingAccountName}`,
      rawReportingAccountName,
      reportingAccountName: displayReportingAccountName,
      formattedReportingAccountName: node.formattedReportingAccountName,
      ...formattedDatas,
      datas: formattedDatas,
      cellStyles:
        rawReportingAccountName ===
          ADVANCED_NII_ACCOUNT_NAMES.TOTAL_RECEIVABLES ||
        rawReportingAccountName === ADVANCED_NII_ACCOUNT_NAMES.TOTAL_PAYABLES ||
        isSummaryRow
          ? {
              fontWeight: "bold",
              backgroundColor: "#fcfcfc",
            }
          : {},
      children: Array.isArray(node.subAccounts)
        ? node.subAccounts.map(processNode).filter(Boolean)
        : [],
    };
  };

  return nestedData.map(processNode).filter(Boolean);
};

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(Number(value))) return "-";
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const buildSummaryRowsForTab = (
  tabPropertyType,
  tabSummaryLabel,
  totalValue,
  latestPlan,
) => {
  const parallelShiftProfit = latestPlan !== 0 ? totalValue / latestPlan : 0;

  if (tabPropertyType === ADVANCED_NII_PROPERTY_TYPES.BALANCE) {
    return [];
  }

  if (tabPropertyType === ADVANCED_NII_PROPERTY_TYPES.NII_BASE) {
    return [
      {
        name: "12mths Earnings at Base",
        value: formatNumber(totalValue, 3),
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color:
              parseFloat(formatNumber(totalValue, 3)) < 0 ? "red" : "black",
          },
        },
      },
      {
        name: LATEST_ANNUAL_NII_FORECAST,
        value: formatNumber(latestPlan, 3),
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color:
              parseFloat(formatNumber(latestPlan, 3)) < 0 ? "red" : "black",
          },
        },
      },
    ];
  }

  if (tabPropertyType === ADVANCED_NII_PROPERTY_TYPES.NII_UP100) {
    return [
      {
        name: "12mths Earnings at UP100",
        value: formatNumber(totalValue, 3),
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color:
              parseFloat(formatNumber(totalValue, 3)) < 0 ? "red" : "black",
          },
        },
      },
      {
        name: LATEST_ANNUAL_NII_FORECAST,
        value: formatNumber(latestPlan, 3),
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color:
              parseFloat(formatNumber(latestPlan, 3)) < 0 ? "red" : "black",
          },
        },
      },
    ];
  }

  return [
    {
      name: TWELVE_MOHTHS_EARNING_AT_RISK,
      value: formatNumber(totalValue, 3),
      cellStyles: { fontWeight: "bold" },
      dynamicCellStyles: {
        value: {
          color: parseFloat(formatNumber(totalValue, 3)) < 0 ? "red" : "black",
        },
      },
    },
    {
      name: LATEST_ANNUAL_NII_FORECAST,
      value: formatNumber(latestPlan, 3),
      cellStyles: { fontWeight: "bold" },
      dynamicCellStyles: {
        value: {
          color: parseFloat(formatNumber(latestPlan, 3)) < 0 ? "red" : "black",
        },
      },
    },
    {
      name: HUNDRED_BP_PARALLEL_SHIFT,
      value: `${parallelShiftProfit.toFixed(2)}%`,
      cellStyles: { fontWeight: "bold" },
      dynamicCellStyles: {
        value: {
          color:
            parseFloat(parallelShiftProfit.toFixed(2)) < 0 ? "red" : "black",
        },
      },
    },
  ];
};

const buildExportSummaryForTab = (
  tabNodes,
  tabPropertyType,
  latestPlan,
  selectedMonthEnd,
) => {
  const flattenNodes = (nodes, rows = []) => {
    (nodes || []).forEach((node) => {
      rows.push(node);
      if (node.subAccounts?.length) {
        flattenNodes(node.subAccounts, rows);
      }
    });
    return rows;
  };

  const allRows = flattenNodes(tabNodes);

  const receivablesRow = allRows.find(
    (row) =>
      row.reportingAccountName === ADVANCED_NII_ACCOUNT_NAMES.TOTAL_RECEIVABLES,
  );

  const payablesRow = allRows.find(
    (row) =>
      row.reportingAccountName === ADVANCED_NII_ACCOUNT_NAMES.TOTAL_PAYABLES,
  );

  let totalValue = 0;

  if (receivablesRow && payablesRow) {
    const allKeys = new Set([
      ...Object.keys(receivablesRow.datas || {}),
      ...Object.keys(payablesRow.datas || {}),
    ]);

    const totalIncomeSensitivity = {};
    allKeys.forEach((key) => {
      const receivableValue = Number(receivablesRow.datas?.[key] || 0);
      const payableValue = Number(payablesRow.datas?.[key] || 0);
      totalIncomeSensitivity[key] = receivableValue + payableValue;
    });

    const selectedMonthKey = selectedMonthEnd?.format("MM/DD/YYYY");

    // Skips adding the select month value
    totalValue = Object.entries(totalIncomeSensitivity).reduce(
      (acc, [key, value]) =>
        key === selectedMonthKey ? acc : acc + Number(value || 0),
      0,
    );
  }

  return buildSummaryRowsForTab(tabPropertyType, "", totalValue, latestPlan);
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const AdvancedNIISensitivityReports = ({
  toSave = false,
  toCompare = false,
  isWritableSfc = false,
  appliedFilters = null,
} = {}) => {
  const { classes } = useStyles(toCompare);
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();

  const {
    hrFiltersDataLoading,
    advancedNIIResultDataLoading,
    advancedNIIResultDataError,
    advancedNIIResultData,
    setAdvancedNIIResultData,
    selectedBusinessEntityID,
    localCurrency,
    selectedModelDatabase,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,
    selectedMonthEnd,
    fetchAdvancedNIIResultReport,
  } = useHedgeRatioContext();

  const {
    fetchApprovalFlowData,
    approvalFlow,
    setApprovalFlow,
    saveApprovalData,
    sendApproverEmailNotifyForSelectedReport,
    sendEmailNotifyLoading,
  } = useApprovalContext();

  const { email } = selectedUserDetail || {};
  const lastSubmittedFiltersRef = useRef(null);

  const APPROVAL_FLOW_PARAMS = useMemo(() => {
    return {
      region: null,
      businessEntityID: selectedBusinessEntityID,
      selectedReportDate: selectedMonthEnd,
      isGlobal: true,
      isModelerReport: false,
      isTreasuryKRIReport: true,
      isKRISummaryReport: false,
      isSFCKRIGlobalSummaryReport: false,
      isRegionKRIGlobalSummaryReport: false,
      isTFSCKRIGlobalSummaryReport: false,
      measure: MEASURE.HUNDRED_BP_PARALLEL_SHIFT_KRI,
      dimension: DIMENSION.INTEREST_RATE_RISK,
      dimension2: null,
    };
  }, [selectedBusinessEntityID, selectedMonthEnd]);

  useEffect(() => {
    if (!appliedFilters) return;

    if (
      !appliedFilters?.selectedBusinessEntityID ||
      !appliedFilters?.selectedModelDatabase ||
      !appliedFilters?.selectedMonthEnd
    ) {
      setAdvancedNIIResultData([]);
      setApprovalFlow([]);
      return;
    }

    const currentSignature = JSON.stringify({
      selectedBusinessEntityID: appliedFilters.selectedBusinessEntityID,
      selectedModelDatabase: appliedFilters.selectedModelDatabase,
      selectedMonthEnd: appliedFilters.selectedMonthEnd?.format("YYYY-MM-DD"),
      advancedNII: appliedFilters.advancedNII,
      selectedFigureDivisor: appliedFilters.selectedFigureDivisor,
      selectedDecimalsIn: appliedFilters.selectedDecimalsIn,
    });

    if (lastSubmittedFiltersRef.current === currentSignature) {
      return;
    }

    lastSubmittedFiltersRef.current = currentSignature;

    fetchAdvancedNIIResultReport(ADVANCED_NII);

    fetchApprovalFlowData({
      ...APPROVAL_FLOW_PARAMS,
      businessEntityID: appliedFilters.selectedBusinessEntityID,
      selectedReportDate: appliedFilters.selectedMonthEnd,
    });
  }, [
    appliedFilters,
    fetchAdvancedNIIResultReport,
    fetchApprovalFlowData,
    setAdvancedNIIResultData,
    setApprovalFlow,
    APPROVAL_FLOW_PARAMS,
  ]);

  const mappedFigureDivisors = useMemo(
    () => (figureDivisors || []).map((x) => ({ id: x.value, name: x.name })),
    [figureDivisors],
  );

  const mappedDecimalsIn = useMemo(
    () => (decimalsIn || []).map((x) => ({ id: x.value, name: x.name })),
    [decimalsIn],
  );

  const latestAnnualForecast = useMemo(
    () => getLatestAnnualNIIForecast(advancedNIIResultData),
    [advancedNIIResultData],
  );

  const selectedTabConfig =
    ADVANCED_NII_TAB_CONFIG[value] || ADVANCED_NII_TAB_CONFIG[0];

  const selectedTabNodes = useMemo(
    () =>
      getAccountNodeByPropertyType(
        advancedNIIResultData,
        selectedTabConfig.propertyType,
      ),
    [advancedNIIResultData, selectedTabConfig.propertyType],
  );

  const tableData = useMemo(() => {
    const excludeNames =
      selectedTabConfig.propertyType === ADVANCED_NII_PROPERTY_TYPES.BALANCE
        ? [ADVANCED_NII_ACCOUNT_NAMES.LATEST_ANNUAL_NII_FORECAST]
        : [];

    const baseData = flattenReportData(
      selectedTabNodes,
      selectedFigureDivisor,
      selectedDecimalsIn,
      excludeNames,
      selectedTabConfig.summaryLabel,
    );

    return addTotalIncomeSensitivityRow(
      baseData,
      selectedTabConfig.summaryLabel,
    );
  }, [
    selectedTabNodes,
    selectedFigureDivisor,
    selectedDecimalsIn,
    selectedTabConfig,
  ]);

  const tableColumns = useMemo(() => {
    const firstNode = tableData?.[0];
    const dynamicColumns = firstNode?.datas
      ? Object.keys(firstNode.datas).map((key) => ({
          id: key,
          label: key,
          minWidth: 130,
          align: "right",
        }))
      : [];

    return [
      {
        id: "reportingAccountName",
        label: "Reporting Account Name",
        minWidth: 240,
        align: "left",
      },
      ...dynamicColumns,
    ];
  }, [tableData]);

  const handleSendApproverEmailNotify = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      sendApproverEmailNotifyForSelectedReport({
        ...APPROVAL_FLOW_PARAMS,
        isGlobal: null,
        isModelerReport: null,
        isTreasuryKRIReport: null,
        isKRISummaryReport: null,
        isSFCKRIGlobalSummaryReport: null,
        isRegionKRIGlobalSummaryReport: null,
        isTFSCKRIGlobalSummaryReport: null,
      });
    }
  };

  const handleStepApproval = ({
    currentStep,
    currentApprover,
    type,
    comment,
    reportDate,
  } = {}) => {
    return saveApprovalData({
      currentApprover,
      type,
      comment,
      reportDate,
    })
      .then(() => fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS }))
      .then(() =>
        sendApproverEmailNotifyForSelectedReport({
          ...APPROVAL_FLOW_PARAMS,
          isGlobal: null,
          isModelerReport: null,
          isTreasuryKRIReport: null,
          isKRISummaryReport: null,
          isSFCKRIGlobalSummaryReport: null,
          isRegionKRIGlobalSummaryReport: null,
          isTFSCKRIGlobalSummaryReport: null,
        }),
      );
  };

  const summaryColumns = useMemo(() => {
    return [
      {
        label: `${selectedTabConfig.summaryLabel} for ${selectedMonthEnd?.format("MM/DD/YYYY")}`,
        children: [
          {
            id: "name",
            label: "Name",
            minWidth: 220,
          },
          {
            id: "value",
            label: "Value",
            minWidth: 120,
            align: "right",
          },
        ],
      },
    ];
  }, [selectedMonthEnd, selectedTabConfig.summaryLabel]);

  const summaryData = useMemo(() => {
    const totalRow = tableData?.find(
      (row) => row.reportingAccountName === selectedTabConfig.summaryLabel,
    );

    const selectedMonthKey = selectedMonthEnd?.format("MM/DD/YYYY");

    // Skips adding the select month value
    const totalValue = Object.entries(totalRow?.datas || {}).reduce(
      (acc, [key, value]) =>
        key === selectedMonthKey ? acc : acc + Number(value || 0),
      0,
    );

    const formattedlatestPlan = applyFilterForValues(
      latestAnnualForecast.value,
      selectedFigureDivisor,
      selectedDecimalsIn,
    );

    return buildSummaryRowsForTab(
      selectedTabConfig.propertyType,
      selectedTabConfig.summaryLabel,
      totalValue,
      formattedlatestPlan,
    );
  }, [
    tableData,
    selectedTabConfig.propertyType,
    selectedTabConfig.summaryLabel,
    latestAnnualForecast.value,
    selectedFigureDivisor,
    selectedDecimalsIn,
    selectedMonthEnd,
  ]);

  const summaryDataByTab = useMemo(() => {
    const result = {};

    const balanceNodes = getAccountNodeByPropertyType(
      advancedNIIResultData,
      ADVANCED_NII_PROPERTY_TYPES.BALANCE,
    );

    const latestAnnualNode = balanceNodes?.find(
      (node) =>
        node.reportingAccountName ===
        ADVANCED_NII_ACCOUNT_NAMES.LATEST_ANNUAL_NII_FORECAST,
    );

    const latestPlan = Number(
      Object.values(latestAnnualNode?.datas || {})?.[0] || 0,
    );

    ADVANCED_NII_TAB_CONFIG.forEach((tab) => {
      const tabNodes = getAccountNodeByPropertyType(
        advancedNIIResultData,
        tab.propertyType,
      );

      result[tab.propertyType] = buildExportSummaryForTab(
        tabNodes,
        tab.propertyType,
        latestPlan,
        selectedMonthEnd,
      );
    });

    return result;
  }, [advancedNIIResultData, selectedMonthEnd]);

  return (
    <>
      {!advancedNIIResultDataError &&
      advancedNIIResultData &&
      advancedNIIResultData?.length > 0 ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              pr: "10px",
            }}
          >
            {approvalFlow?.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSendApproverEmailNotify}
                sx={{
                  fontSize: "0.8rem",
                  boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                  height: "38px",
                  mt: "-10px",
                }}
                disabled={
                  selectedUserDetailLoading ||
                  hrFiltersDataLoading ||
                  advancedNIIResultDataLoading ||
                  !selectedBusinessEntityID ||
                  !selectedMonthEnd ||
                  sendEmailNotifyLoading
                }
              >
                Send Reminder Notification
              </Button>
            )}
          </Box>

          <ProgressSteps
            stepsData={approvalFlow}
            isGlobal={true}
            isModelerReport={false}
            isTreasuryKRIReport={true}
            isKRISummaryReport={false}
            isSFCKRIGlobalSummaryReport={false}
            isRegionKRIGlobalSummaryReport={false}
            isTFSCKRIGlobalSummaryReport={false}
            reportDate={selectedMonthEnd}
            loggedInEmail={email}
            handleStepApproval={handleStepApproval}
          />
        </>
      ) : (
        <Typography
          sx={{
            color: "#6a6a6a",
            marginBottom: 2,
            fontStyle: "italic",
          }}
        >
          Please generate the report to view the Approval Progress for Advanced
          NII Sensitivity Report
        </Typography>
      )}

      {localCurrency && (
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: "#6a6a6a",
            fontWeight: "normal",
            fontStyle: "italic",
            marginTop: -1,
            marginBottom: 2,
          }}
        >
          (The below figures are shown in {localCurrency})
        </Typography>
      )}

      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Figures In"
            labelId="show-figures-in"
            options={mappedFigureDivisors}
            selectedValue={selectedFigureDivisor}
            handleChange={setSelectedFigureDivisor}
            placeholder={"Show Figures In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              advancedNIIResultDataLoading
            }
          />
        </Grid>

        {localCurrency && (
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id="currency-type">Currency</InputLabel>
            <Typography
              sx={{
                color: "#6a6a6a",
                fontWeight: "bold",
                marginTop: 1,
              }}
            >
              {localCurrency}
            </Typography>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Decimal In"
            labelId="show-decimal-in"
            options={mappedDecimalsIn}
            selectedValue={selectedDecimalsIn}
            handleChange={setSelectedDecimalsIn}
            placeholder={"Show Decimals In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              advancedNIIResultDataLoading
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <div className={classes.nIISensitivityExportBtn}>
            <AdvancedNIISensitivityExport
              advancedNIIResultData={advancedNIIResultData}
              summaryDataByTab={summaryDataByTab}
              tabConfig={ADVANCED_NII_TAB_CONFIG}
              disabled={
                !tableColumns ||
                !tableColumns?.length ||
                !tableData ||
                !tableData?.length ||
                advancedNIIResultDataLoading ||
                advancedNIIResultDataError
              }
            />
          </div>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {!advancedNIIResultDataLoading && advancedNIIResultDataError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : !advancedNIIResultDataLoading &&
            !advancedNIIResultData?.length ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : advancedNIIResultDataLoading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={300}
              animation="pulse"
              style={{ marginBottom: 8 }}
            />
          ) : (
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Advanced NII Sensitivity
              </Typography>

              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="tabs"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                  },
                }}
              >
                {ADVANCED_NII_TAB_CONFIG.map((tab) => (
                  <Tab key={tab.label} label={tab.label} />
                ))}
              </Tabs>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {selectedTabConfig.summaryLabel}
                </Typography>

                <ColumnGroupingTable
                  columns={tableColumns}
                  datas={tableData}
                  loading={false}
                  tableHeight={350}
                  freezeParentColumnCount={1}
                  disablePagination={true}
                  applyThousandsSeparator={true}
                />

                {selectedTabConfig.propertyType !==
                  ADVANCED_NII_PROPERTY_TYPES.BALANCE && (
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      marginBottom: 0,
                      width: "50%",
                      margin: "0 auto",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                      <ColumnGroupingTable
                        columns={summaryColumns}
                        datas={summaryData}
                        loading={false}
                        disablePagination={true}
                        applyThousandsSeparator={true}
                        tableHeight={summaryData.length >= 3 ? 151 : 120}
                        hideChildRowHeader
                        freezeParentColumnCount={1}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default AdvancedNIISensitivityReports;
